import {
  fetchSingleEnvelope,
  selectCampaignEnvelopeReview,
  selectCommunication,
  setURLParams,
  showSuccessAlert,
} from 'actions';
import { setEnvelopePreview } from 'actions/envelopeListView';
import { showMoreLikeThis } from 'actions/envelopeView';
import EnvelopeSkeleton from 'components/Envelope/EnvelopeSkeleton';
import RenderEnvelopeBoxes from 'components/Envelope/RenderEnvelopeBoxes';
import Permissions from 'components/Permissions';
import { CLOSE_MODAL_ICON } from 'constants/commonIcons';
import { COPY_ID_ICON, LINK_ICON } from 'constants/envelopeIcons';
import { concatFromQueryParam } from 'constants/eventsUtils';
import { SEARCH_ICON } from 'constants/filterIcons';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getCustomerDomain } from 'selectors/auth';
import { getCommunicationPlatformGuid } from 'selectors/communications';
import { getEnvelopeToPreview } from 'selectors/envelopeListView';
import {
  CampaignWithRules,
  TRANSLATION_COMM_TYPES,
  getCampaignsRulesFromCommunication,
  getEnvelopeVersions,
  getSelectedCampaign,
  getSelectedCommunicationWithEvents,
  getTranslationFromCommunication,
} from 'selectors/envelopeReview';
import { getEnvelopes, getFetchSingleEnvelopeLoading } from 'selectors/envelopes';
import { getTranslationMode } from 'selectors/envelopeView';
import { getUserPreferOriginalLanguages } from 'selectors/users';
import { useSelector } from 'store';
import { useHistory } from 'utils/urls';
import { getLikelyLanguage, shouldPreferOriginal } from 'utils/userMetadata';
import EnvelopePreviewMatchNavigation from './EnvelopePreviewMatchNavigation';

const sortCampaigns = (
  [, a]: [string, CampaignWithRules],
  [, b]: [string, CampaignWithRules]
): number => {
  const aPrio = a.campaignPriority && a.campaignPriority !== 0 ? a.campaignPriority : 1000;
  const bPrio = b.campaignPriority && b.campaignPriority !== 0 ? b.campaignPriority : 1000;

  if (aPrio < bPrio) {
    return -1;
  }
  if (bPrio > aPrio) {
    return 1;
  }

  if (a.campaignName < b.campaignName) {
    return -1;
  }
  if (a.campaignName > b.campaignName) {
    return 1;
  }

  return 0;
};

const EnvelopePreview = (): JSX.Element | null => {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const customerDomain = useSelector(getCustomerDomain);
  const envelopeToPreview = useSelector(getEnvelopeToPreview);
  const envelopeLoading = useSelector(getFetchSingleEnvelopeLoading);

  const envelopes = useSelector(getEnvelopes);
  const envelopeIdx = envelopes.findIndex((e) => e.envelope.uuid === envelopeToPreview?.envelopeId);

  const nextEnvelopeIdx = envelopeIdx + 1;
  const nextEnvelope = envelopes[nextEnvelopeIdx] ?? null;

  const prevEnvelopeIdx = envelopeIdx - 1;
  const prevEnvelope = envelopes[prevEnvelopeIdx] ?? null;

  const communicationPlatformGuid = useSelector(getCommunicationPlatformGuid);

  const selectedCommunication = useSelector(getSelectedCommunicationWithEvents);
  const selectedCampaign = useSelector(getSelectedCampaign);

  const campaignsRules = useSelector((state) =>
    getCampaignsRulesFromCommunication(state, selectedCommunication?.uuid || '')
  );

  const envelopeVersions = useSelector(getEnvelopeVersions);
  const version =
    envelopeVersions.find((c) => !TRANSLATION_COMM_TYPES.includes(c.communication_type)) ||
    envelopeVersions[0];

  const translationMode = useSelector(getTranslationMode);
  const preferOriginalLanguages = useSelector(getUserPreferOriginalLanguages);
  const envelopeTranslation = useSelector((state) =>
    getTranslationFromCommunication(state, version?.uuid || '')
  );
  const preferOriginal = shouldPreferOriginal(
    preferOriginalLanguages,
    getLikelyLanguage(version) || getLikelyLanguage(envelopeTranslation)
  );

  // cleanup
  useEffect(
    () => () => {
      dispatch(setEnvelopePreview(null));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (!campaignsRules) return;

    const campaigns = Object.entries(campaignsRules).sort(sortCampaigns);
    const foundCampaign = campaigns.find((c) => c[0] === selectedCampaign);

    if (campaigns && campaigns[0] && !foundCampaign) {
      dispatch(selectCampaignEnvelopeReview(campaigns[0][0]));
    }
  }, [campaignsRules, dispatch, selectedCampaign]);

  useEffect(() => {
    if (!version) return;
    const useTranslation = !preferOriginal && translationMode && envelopeTranslation;
    dispatch(
      selectCommunication({ communication: useTranslation ? envelopeTranslation : version })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version?.uuid, dispatch, preferOriginal, translationMode, envelopeTranslation?.uuid]);

  if (!envelopeToPreview) return null;

  const handleClosePreview = (): void => {
    dispatch(setEnvelopePreview(null));
    dispatch(selectCommunication({ communication: null }));
    dispatch(setURLParams({ envelopes__envelope_preview: '' }));
  };

  const handleCopyUrl = async (): Promise<void> => {
    await navigator.clipboard.writeText(
      `${window.location.origin}/${customerDomain}/communication-envelopes/${envelopeToPreview.envelopeId}`
    );
    dispatch(showSuccessAlert('URL copied to your clipboard'));
  };

  const handleCopyId = async (): Promise<void> => {
    await navigator.clipboard.writeText(communicationPlatformGuid ?? '');
    dispatch(showSuccessAlert('ID copied to your clipboard'));
  };

  const queryParams = concatFromQueryParam(
    location.search,
    location.pathname,
    resourceQueryParamName.envelopes
  );

  const handlePreviewReviewMessage = (): void => {
    dispatch(setEnvelopePreview(null));
    history.pushLookup({
      customerDomain,
      routeName: 'envelope-detail',
      routeParams: { envelopeId: envelopeToPreview.envelopeId },
      queryParams: `${queryParams}&threads__entry_point=${envelopeToPreview.envelopeId}`,
    });
  };

  const handleOpenMoreLikeThis = (): void => {
    dispatch(showMoreLikeThis(true));
  };

  const handleNext = (): void => {
    dispatch(setURLParams({ envelopes__envelope_preview: nextEnvelope.envelope.uuid }));

    dispatch(
      setEnvelopePreview({ envelopeId: nextEnvelope.envelope.uuid, envelopeIdx: nextEnvelopeIdx })
    );
    dispatch(fetchSingleEnvelope({ envelopeId: nextEnvelope.envelope.uuid, preview: true }));
  };

  const handleBack = (): void => {
    dispatch(setURLParams({ envelopes__envelope_preview: prevEnvelope.envelope.uuid }));
    dispatch(
      setEnvelopePreview({ envelopeId: prevEnvelope.envelope.uuid, envelopeIdx: prevEnvelopeIdx })
    );
    dispatch(fetchSingleEnvelope({ envelopeId: prevEnvelope.envelope.uuid, preview: true }));
  };

  return (
    <div
      className="absolute right-0 h-full w-148 flex flex-col justify-between gap-1 bg-white border-l border-litlingo-gray-2 overflow-hidden z-20"
      style={{ filter: 'drop-shadow(3px 1px 4px rgba(0, 0, 0, 0.25))' }}
    >
      <div
        className="pt-8 pb-4 flex flex-col gap-1 bg-white"
        style={{ boxShadow: '5px 6px 18px 0 rgba(0, 0, 0, 0.10)' }}
      >
        <div className="flex flex-row gap-2 px-8">
          <div className="flex flex-1 items-start">
            <span className="text-heading-2" id="modal-headline">
              {`Message View: #${envelopeToPreview.envelopeIdx + 1}`}
            </span>
          </div>

          <div className="flex">
            <button
              type="button"
              data-testid="close-button"
              className="w-6 z-10 focus:outline-none"
              aria-label="Close"
              onClick={handleClosePreview}
            >
              {CLOSE_MODAL_ICON}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2 text-body mt-1 px-8">
          <Permissions action="communications.seeCommunicationSlug">
            <button type="button" className="focus:outline-none" onClick={handleCopyId}>
              {COPY_ID_ICON}
            </button>
          </Permissions>
          <button type="button" className="focus:outline-none" onClick={handleCopyUrl}>
            {LINK_ICON}
          </button>

          <Permissions action="communication_envelopes.more_like_this">
            <button type="button" className="focus:outline-none" onClick={handleOpenMoreLikeThis}>
              <div className="w-5">{SEARCH_ICON()}</div>
            </button>
          </Permissions>

          <button
            type="button"
            className="underline text-litlingo-primary-120 focus:outline-none"
            onClick={handlePreviewReviewMessage}
          >
            <span>Review Message</span>
          </button>
        </div>
        <div className="px-8 mt-5 flex flex-col items-end">
          <EnvelopePreviewMatchNavigation />
        </div>
      </div>
      {envelopeLoading ? (
        <EnvelopeSkeleton />
      ) : (
        <div className="pl-2 overflow-hidden h-full">
          <RenderEnvelopeBoxes />
        </div>
      )}

      <div
        className="py-4 px-8 flex flex-row justify-between bg-white"
        style={{ boxShadow: '3px 1px 8px rgba(0, 0, 0, 0.25)' }}
      >
        <span>
          Message {envelopeIdx + 1} of {envelopes.length}
        </span>
        <div className="flex flex-row gap-2">
          <button
            type="button"
            className="button button--secondary h-8 w-27.5 justify-center"
            onClick={handleBack}
            disabled={prevEnvelopeIdx < 0}
          >
            <span className="font-bold">Back</span>
          </button>
          <button
            type="button"
            className="button button--primary h-8 w-27.5 justify-center"
            onClick={handleNext}
            disabled={nextEnvelopeIdx === envelopes.length}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnvelopePreview;
